export function loadImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = source;
  });
}

export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function isIOSDevice() {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export async function createCompositeBlob({
  imageSrc,
  cropPixels,
  overlayUrl,
  exportOptions,
}) {
  const image = await loadImage(imageSrc);
  const overlay = await loadImage(overlayUrl);

  const width = Number(exportOptions?.width) || 1080;
  const height = Number(exportOptions?.height) || 1080;
  const quality = Number(exportOptions?.quality) || 1;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d', { alpha: true });
  if (!context) {
    throw new Error('Canvas context is unavailable.');
  }

  context.clearRect(0, 0, width, height);

  // Keep the circular look from the original plugin before drawing overlay.
  const outerRadius = Math.min(width, height) / 2;
  context.save();
  context.beginPath();
  context.arc(width / 2, height / 2, outerRadius, 0, Math.PI * 2);
  context.clip();

  context.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    width,
    height
  );
  context.restore();

  context.drawImage(overlay, 0, 0, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to generate PNG output.'));
          return;
        }
        resolve(blob);
      },
      'image/png',
      quality
    );
  });
}

export async function triggerDownload(blob, filename) {
  if (typeof window === 'undefined') {
    return;
  }

  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      const sharedFile = new File([blob], filename, { type: 'image/png' });
      if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [sharedFile] })) {
        await navigator.share({
          title: filename,
          files: [sharedFile],
        });
        return;
      }
    } catch (error) {
      // Continue with browser-specific download fallbacks.
    }
  }

  const url = URL.createObjectURL(blob);

  if (isIOSDevice()) {
    const previewLink = document.createElement('a');
    previewLink.href = url;
    previewLink.target = '_blank';
    previewLink.rel = 'noopener';
    document.body.appendChild(previewLink);
    previewLink.click();
    previewLink.remove();

    setTimeout(() => URL.revokeObjectURL(url), 30000);
    return;
  }

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
